import axios from 'axios';
import { useAuthStore } from '../stores/auth-store';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

let refreshInFlight: Promise<string> | null = null;

// Consume each rotating refresh token once across concurrent requests.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const isAuthAttempt = ['/auth/login', '/auth/verify-2fa', '/auth/refresh'].includes(
      originalRequest?.url,
    );
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !isAuthAttempt &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        if (!refreshInFlight) {
          refreshInFlight = (async () => {
            const token = useAuthStore.getState().refreshToken;
            if (!token) throw new Error('No refresh token');
            const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
              refreshToken: token,
            });
            if (useAuthStore.getState().refreshToken !== token)
              throw new Error('Account changed during refresh');
            useAuthStore.getState().setTokens(data.accessToken, data.refreshToken);
            return data.accessToken as string;
          })().finally(() => {
            refreshInFlight = null;
          });
        }
        originalRequest.headers.Authorization = `Bearer ${await refreshInFlight}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

// API methods
export const authApi = {
  login: async (email: string, password: string) => {
    const { data } = await apiClient.post('/auth/login', { email, password });
    return data;
  },
  verify2FA: async (code: string, tempToken: string) => {
    const { data } = await apiClient.post('/auth/verify-2fa', { code, tempToken });
    return data;
  },
  getProfile: async () => {
    const { data } = await apiClient.get('/auth/profile');
    return data;
  },
  logout: async (refreshToken: string) => {
    const { data } = await apiClient.post('/auth/logout', { refreshToken });
    return data;
  },
};

export const adminApi = {
  getDashboardStats: async () => {
    const { data } = await apiClient.get('/admin/dashboard');
    return data;
  },
  getPendingRegistrations: async (page = 1, limit = 20) => {
    const { data } = await apiClient.get('/admin/registrations/pending', {
      params: { page, limit },
    });
    return data;
  },
  approveRegistration: async (memberId: string) => {
    const { data } = await apiClient.post('/admin/registrations/approve', {
      memberId,
      photoIdVerified: true,
    });
    return data;
  },
  rejectRegistration: async (memberId: string, reason?: string) => {
    const { data } = await apiClient.post('/admin/registrations/reject', { memberId, reason });
    return data;
  },
  getMembers: async (page = 1, limit = 20, status?: string, search?: string) => {
    const { data } = await apiClient.get('/admin/members', {
      params: { page, limit, status, search },
    });
    return data;
  },
  getMember: async (memberId: string) => {
    const { data } = await apiClient.get(`/admin/members/${memberId}`);
    return data;
  },
  updateMember: async (
    memberId: string,
    updates: { firstName?: string; lastName?: string; email?: string; phone?: string },
  ) => {
    const { data } = await apiClient.patch(`/admin/members/${memberId}`, updates);
    return data;
  },
  updateMemberStatus: async (memberId: string, status: string, reason?: string) => {
    const { data } = await apiClient.patch(`/admin/members/${memberId}/status`, { status, reason });
    return data;
  },
  getMemberPhotoIdUrl: async (memberId: string) => {
    const { data } = await apiClient.get(`/admin/members/${memberId}/photo-id`);
    return data;
  },
  createInviteToken: async (expiresInDays?: number, maxUses?: number) => {
    const { data } = await apiClient.post('/admin/invite-tokens', { expiresInDays, maxUses });
    return data;
  },
  getInviteTokens: async (page = 1, limit = 20) => {
    const { data } = await apiClient.get('/admin/invite-tokens', { params: { page, limit } });
    return data;
  },
  deactivateInviteToken: async (tokenId: string) => {
    const { data } = await apiClient.patch(`/admin/invite-tokens/${tokenId}/deactivate`);
    return data;
  },
};

export const formsApi = {
  getAllForms: async (status?: string) => {
    const { data } = await apiClient.get('/forms/admin/all', { params: { status } });
    return data;
  },
  getFormSubmissions: async (formId: string, page = 1, limit = 20) => {
    const { data } = await apiClient.get(`/forms/admin/${formId}/submissions`, {
      params: { page, limit },
    });
    return data;
  },
  updateSubmissionStatus: async (submissionId: string, status: string, reviewNotes?: string) => {
    const { data } = await apiClient.patch(`/forms/admin/submissions/${submissionId}/status`, {
      status,
      reviewNotes,
    });
    return data;
  },
  createForm: async (title: string, description: string, schema: any) => {
    const { data } = await apiClient.post('/forms/admin', { title, description, schema });
    return data;
  },
  updateForm: async (formId: string, title: string, description: string, schema: any) => {
    const { data } = await apiClient.patch(`/forms/admin/${formId}`, {
      title,
      description,
      schema,
    });
    return data;
  },
  updateFormStatus: async (formId: string, status: string) => {
    const { data } = await apiClient.patch(`/forms/admin/${formId}/status`, { status });
    return data;
  },
  deleteForm: async (formId: string) => {
    const { data } = await apiClient.delete(`/forms/admin/${formId}`);
    return data;
  },
};

export const contentApi = {
  getAllContent: async (type?: string, status?: string, page = 1, limit = 20) => {
    const { data } = await apiClient.get('/content/admin/all', {
      params: { type, status, page, limit },
    });
    return data;
  },
  createContent: async (
    type: string,
    title: string,
    body: string,
    categoryIds: string[],
    scheduledFor?: string,
  ) => {
    const { data } = await apiClient.post('/content/admin', {
      type,
      title,
      body,
      categoryIds,
      scheduledFor,
    });
    return data;
  },
  updateContent: async (
    contentId: string,
    title: string,
    body: string,
    categoryIds: string[],
    scheduledFor?: string,
  ) => {
    const { data } = await apiClient.patch(`/content/admin/${contentId}`, {
      title,
      body,
      categoryIds,
      scheduledFor,
    });
    return data;
  },
  publishContent: async (contentId: string) => {
    const { data } = await apiClient.patch(`/content/admin/${contentId}/publish`);
    return data;
  },
  unpublishContent: async (contentId: string) => {
    const { data } = await apiClient.patch(`/content/admin/${contentId}/unpublish`);
    return data;
  },
  archiveContent: async (contentId: string) => {
    const { data } = await apiClient.patch(`/content/admin/${contentId}/archive`);
    return data;
  },
  deleteContent: async (contentId: string) => {
    const { data } = await apiClient.delete(`/content/admin/${contentId}`);
    return data;
  },
  getCategories: async (type?: string) => {
    const { data } = await apiClient.get('/content/categories/all', { params: { type } });
    return data;
  },
  createCategory: async (slug: string, name: string, contentType: string) => {
    const { data } = await apiClient.post('/content/admin/categories', { slug, name, contentType });
    return data;
  },
  updateCategory: async (categoryId: string, name: string) => {
    const { data } = await apiClient.patch(`/content/admin/categories/${categoryId}`, { name });
    return data;
  },
  deleteCategory: async (categoryId: string) => {
    const { data } = await apiClient.delete(`/content/admin/categories/${categoryId}`);
    return data;
  },
};

// Meetings API (admin)
export const meetingsApi = {
  getParticipants: async (
    id: string,
  ): Promise<Array<{ identity: string; name: string; microphoneMuted: boolean }>> => {
    const { data } = await apiClient.get(`/meetings/${id}/participants`);
    return data;
  },
  muteParticipant: async (id: string, memberId: string) => {
    await apiClient.post(`/meetings/${id}/participants/${memberId}/mute`);
  },
  disconnectParticipant: async (id: string, memberId: string) => {
    await apiClient.post(`/meetings/${id}/participants/${memberId}/disconnect`);
  },
  joinMeeting: async (id: string, name: string): Promise<{ token: string; url: string }> => {
    const { data } = await apiClient.post(`/meetings/${id}/join`, { name });
    return data;
  },
  getMeetings: async (status?: string) => {
    const { data } = await apiClient.get('/meetings', { params: { status } });
    return data;
  },
  getMeetingById: async (id: string) => {
    const { data } = await apiClient.get(`/meetings/${id}`);
    return data;
  },
  createMeeting: async (
    title: string,
    description: string,
    scheduledAt: string,
    feedbackFormId?: string,
  ) => {
    const { data } = await apiClient.post('/meetings', {
      title,
      description,
      scheduledAt: new Date(scheduledAt).toISOString(),
      feedbackFormId,
    });
    return data;
  },
  updateMeeting: async (id: string, title: string, description: string, scheduledAt: string) => {
    const { data } = await apiClient.patch(`/meetings/${id}`, {
      title,
      description,
      scheduledAt: new Date(scheduledAt).toISOString(),
    });
    return data;
  },
  deleteMeeting: async (id: string) => {
    const { data } = await apiClient.delete(`/meetings/${id}`);
    return data;
  },
  startMeeting: async (id: string) => {
    const { data } = await apiClient.post(`/meetings/${id}/start`);
    return data;
  },
  endMeeting: async (id: string) => {
    const { data } = await apiClient.post(`/meetings/${id}/end`);
    return data;
  },
  getQuestions: async (meetingId: string, status?: string) => {
    const { data } = await apiClient.get(`/meetings/${meetingId}/questions`, {
      params: { status },
    });
    return data;
  },
  answerQuestion: async (questionId: string, answer: string) => {
    const { data } = await apiClient.patch(`/meetings/questions/${questionId}/answer`, { answer });
    return data;
  },
  hideQuestion: async (questionId: string) => {
    const { data } = await apiClient.patch(`/meetings/questions/${questionId}/hide`);
    return data;
  },
};
